import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import WeeklyPlanReview from "./WeeklyPlanReview";
import { FONT, COLOR, RADIUS, Z } from "../../lib/tokens";
import { insertScheduledMeal } from "../../lib/useScheduledMeals";
import { useToast } from "../../lib/toast";
import { useIngredientInfo } from "../../lib/useIngredientInfo";
import { useWeeklyPlan } from "../../lib/useWeeklyPlan";
import { runWeekPlan, pendingWeekPlan, runSlotReroll } from "../../lib/weeklyPlanRunner";
import { fetchLastCookedMap } from "../../lib/lastCooked";
import { buildBookDigest } from "../../lib/bookDigest";
import { commitWeeklyPlan } from "../../lib/weeklyPlanCommit";
import { computeWeekShoppingItems } from "../../lib/weeklyPlanShopping";
import { findRecipe } from "../../data/recipes";
import { startOfWeek } from "../../lib/calendarUtils";
import { getMeasurementSystem } from "../../lib/unitPrefs";
import {
  PLANNER_GOALS, PLANNER_BUDGETS, PLANNER_SOURCES, PLANNER_NEW_MODES,
  MEAL_SLOTS, SLOT_LABELS, defaultSlots, slotKey, toLocalISODate, dayLabelFor,
} from "./plannerShared";

// WeeklyPlanner — the "plan my week" flow. Two entry points: the +
// (Quick Create) tab and the Calendar's Week view. Owns the whole
// lifecycle:
//
//   SETUP   factor dials (source / goal / budget), household sizing,
//           7×3 slot toggles, explicit PLAN MY WEEK CTA (paid-AI
//           opt-in rule: nothing fires before this tap).
//   REVIEW  WeeklyPlanReview — swap / reroll / lock / remove per slot,
//           then commit to scheduled_meals + sync the shopping list.
//
// FULL-SCREEN overlay (portal), not a bottom sheet — the v1 sheet had
// its only close control scroll away with the content, trapping the
// user. The header (with a labeled CLOSE button) is fixed and present
// on every phase; the body scrolls under it.
//
// `schedule` prop is optional: the Calendar passes its hook's writer
// (keeps its local state live); the + entry omits it and we fall back
// to the standalone insertScheduledMeal — a second useScheduledMeals
// mount here would steal the Calendar's realtime channel (safeChannel
// takes over same-topic channels).
//
// Draft state persists in weekly_plans (useWeeklyPlan) so closing or
// backgrounding mid-review loses nothing.

const kicker = {
  fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
  letterSpacing: "0.14em", color: COLOR.sub, marginBottom: 8,
};

function ChipRow({ options, value, onChange, disabledIds = new Set() }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
      {options.map((o) => {
        const active = o.id === value;
        const disabled = disabledIds.has(o.id);
        return (
          <button
            key={o.id}
            className="mcm-focusable"
            onClick={() => !disabled && onChange(o.id)}
            disabled={disabled}
            style={{
              fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
              letterSpacing: "0.1em",
              color: disabled ? COLOR.sub : active ? COLOR.ink : COLOR.muted,
              background: active ? COLOR.goldDeep : COLOR.ground,
              border: `1px solid ${active ? COLOR.goldDim : COLOR.edge}`,
              borderRadius: RADIUS.pill,
              padding: "8px 14px",
              cursor: disabled ? "default" : "pointer",
              opacity: disabled ? 0.5 : 1,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function WeeklyPlanner({
  userId,
  profile,
  pantry = [],
  shoppingList = [],
  setShoppingList,
  schedule,
  userRecipes = [],
  findUserRecipe,
  familyCount = 0,
  cursorDate,
  onClose,
}) {
  const { push: pushToast } = useToast();
  const { getInfo } = useIngredientInfo();

  const weekStartISO = useMemo(() => {
    const base = startOfWeek(cursorDate || new Date(), getMeasurementSystem());
    return toLocalISODate(base);
  }, [cursorDate]);

  const { draft, loading, createDraft, saveSlots, saveFactors, discardDraft, markCommitted } =
    useWeeklyPlan(userId, weekStartISO);

  // ── Setup state (hydrated from an existing draft once loaded) ─────
  const [sourceId, setSourceId] = useState("mixed");
  const [newMode, setNewMode] = useState("chefs_choice");
  const [goal, setGoal] = useState("balanced");
  const [budget, setBudget] = useState("normal");
  const [servings, setServings] = useState(familyCount > 0 ? Math.max(2, familyCount + 1) : 1);
  const [slots, setSlots] = useState(defaultSlots);
  const [phase, setPhase] = useState("setup"); // setup | planning | review
  const [weekNote, setWeekNote] = useState("");
  const [digest, setDigest] = useState([]);
  const [busySlotKey, setBusySlotKey] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted] = useState(false);
  const [shoppingRows, setShoppingRows] = useState(null);
  const [shoppingAdded, setShoppingAdded] = useState(false);
  const [planError, setPlanError] = useState("");
  const hydratedRef = useRef(false);
  const swappedOutRef = useRef(new Set());
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  // Attach to a plan promise (fresh or resumed) — state updates only
  // land if this mount is still alive; the runner persists the result
  // to the draft row either way (no-waiting-page-of-death rule).
  const attachPlan = (promise) => {
    promise
      .then((res) => {
        if (!aliveRef.current) return;
        setSlots(res.slots);
        setWeekNote(res.weekNote);
        setPhase("review");
      })
      .catch((e) => {
        if (!aliveRef.current) return;
        console.error("[weeklyPlanner] plan failed:", e);
        setPlanError(e?.message || "Planning failed — try again.");
        setPhase("setup");
      });
  };

  useEffect(() => {
    if (loading || hydratedRef.current) return;
    hydratedRef.current = true;
    if (!draft) return;
    const f = draft.factors || {};
    if (f.source?.mode) setNewMode(f.source.mode);
    if (f.source?.book != null) {
      const match = PLANNER_SOURCES.find((s) => Math.abs(s.book - f.source.book) < 0.01);
      if (match) setSourceId(match.id);
    }
    if (f.goal) setGoal(f.goal);
    if (f.budget) setBudget(f.budget);
    if (typeof f.weekNote === "string") setWeekNote(f.weekNote);
    if (draft.household?.servings) setServings(draft.household.servings);
    if (Array.isArray(draft.slots) && draft.slots.length) {
      setSlots(draft.slots);
      if (draft.slots.some((s) => s?.source || s?.error)) setPhase("review");
    }
    // Reopened mid-generation? Rejoin the in-flight plan instead of
    // showing a blank setup while a paid call runs in the background.
    const pending = pendingWeekPlan(draft.id);
    if (pending) {
      setPhase("planning");
      attachPlan(pending);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, draft]);

  // The review screen's macro chips read the book digest, which is
  // built at plan time. After a background completion + reopen the
  // digest state is empty — rebuild it (cheap: lastCooked is cached).
  useEffect(() => {
    if (phase !== "review" || digest.length > 0) return;
    let alive = true;
    (async () => {
      const lastCookedMap = await fetchLastCookedMap(userId);
      if (!alive) return;
      setDigest(buildBookDigest({ userRecipes, lastCookedMap, pantry, getInfo, goal }));
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const factors = useMemo(() => ({
    source: {
      book: PLANNER_SOURCES.find((s) => s.id === sourceId)?.book ?? 0.6,
      mode: newMode,
    },
    goal,
    budget,
  }), [sourceId, newMode, goal, budget]);

  const lockedKeys = useMemo(
    () => new Set(slots.filter((s) => s?.locked).map(slotKey)),
    [slots],
  );

  const updateSlots = (next) => {
    setSlots(next);
    saveSlots(next);
  };

  const bookOnly = sourceId === "book";
  const bookEmpty = userRecipes.length === 0;
  const pantryEmpty = pantry.length === 0;

  // ── PLAN MY WEEK ──────────────────────────────────────────────────
  // The actual call runs in weeklyPlanRunner (module scope) and writes
  // its result to the draft row on completion — closing this screen
  // mid-plan loses nothing; reopening rejoins or finds it finished.
  const runPlan = async () => {
    setPhase("planning");
    setPlanError("");
    try {
      let d = draft;
      if (!d) {
        d = await createDraft({
          weekStart: weekStartISO,
          factors,
          household: { servings },
          slots,
        });
      } else {
        saveFactors({ factors, household: { servings } });
      }
      if (!d?.id) throw new Error("Couldn't start a draft — try again.");

      const lastCookedMap = await fetchLastCookedMap(userId);
      const dg = buildBookDigest({
        userRecipes, lastCookedMap, pantry, getInfo, goal,
      });
      setDigest(dg);

      attachPlan(runWeekPlan({
        draftId: d.id,
        args: {
          mode: "menu",
          factors,
          slots,
          servings,
          pantry,
          profile,
          bookDigest: dg,
        },
      }));
    } catch (e) {
      console.error("[weeklyPlanner] plan failed:", e);
      setPlanError(e?.message || "Planning failed — try again.");
      setPhase("setup");
    }
  };

  // ── Per-slot actions ──────────────────────────────────────────────
  const usedSlugs = useMemo(
    () => new Set(slots.filter((s) => s?.recipeSlug).map((s) => s.recipeSlug)),
    [slots],
  );

  const repickBook = (slotObj) => {
    // Free client-side swap: next digest candidate not already in the
    // plan and not just swapped out of this slot (dusty rows first —
    // the whole point is resurfacing forgotten recipes).
    const candidates = digest.filter(
      (r) => !usedSlugs.has(r.slug) && !swappedOutRef.current.has(r.slug),
    );
    const pick = candidates.find((r) => r.dusty) || candidates[0];
    if (!pick) {
      pushToast("No other book recipes to swap in — try a reroll instead.", { kind: "info" });
      return;
    }
    if (slotObj.recipeSlug) swappedOutRef.current.add(slotObj.recipeSlug);
    updateSlots(slots.map((s) =>
      slotKey(s) === slotKey(slotObj)
        ? {
            day: s.day, slot: s.slot, enabled: true,
            source: "book",
            recipeSlug: pick.slug,
            recipeOwnerUserId: pick.ownerUserId || null,
            title: pick.title,
            reason: pick.dusty ? "swapped in — one you haven't made lately" : "swapped in",
          }
        : s,
    ));
  };

  const rerollConcept = async (slotObj) => {
    const key = slotKey(slotObj);
    if (!draft?.id) return;
    setBusySlotKey(key);
    try {
      const currentTitles = slots
        .filter((s) => s?.title && slotKey(s) !== key)
        .map((s) => s.title);
      // Runner persists the merged result to the draft row even if
      // this screen closes mid-reroll (no lost paid work).
      const merged = await runSlotReroll({
        draftId: draft.id,
        day: slotObj.day,
        slot: slotObj.slot,
        currentSlots: slots,
        args: {
          mode: "reroll",
          factors,
          slots,
          servings,
          pantry,
          profile,
          bookDigest: digest,
          rerollSlots: [{ day: slotObj.day, slot: slotObj.slot }],
          avoidTitles: slotObj.title ? [...currentTitles, slotObj.title] : currentTitles,
          currentTitles,
        },
      });
      if (aliveRef.current) setSlots(merged);
    } catch (e) {
      if (aliveRef.current) pushToast(e?.message || "Reroll failed — try again.", { kind: "warn" });
    } finally {
      if (aliveRef.current) setBusySlotKey(null);
    }
  };

  const onReroll = (slotObj) => {
    if (slotObj.source === "book" && !slotObj.error) repickBook(slotObj);
    else rerollConcept(slotObj);
  };

  const onToggleLock = (slotObj) => {
    updateSlots(slots.map((s) =>
      slotKey(s) === slotKey(slotObj)
        ? { ...s, locked: s.locked ? null : { title: s.title || "" } }
        : s,
    ));
  };

  const onRemove = (slotObj) => {
    updateSlots(slots.map((s) =>
      slotKey(s) === slotKey(slotObj)
        ? { day: s.day, slot: s.slot, enabled: false }
        : s,
    ));
  };

  // ── Commit + shopping ─────────────────────────────────────────────
  const resolveSlotRecipe = (slot) =>
    slot?.source === "book" && slot.recipeSlug
      ? findRecipe(slot.recipeSlug, findUserRecipe, slot.recipeOwnerUserId)
      : null;

  // Calendar entry passes its hook's writer; + entry falls back to the
  // standalone insert (see header comment — channel-steal hazard).
  const scheduleFn = schedule
    || ((args) => insertScheduledMeal({
      userId,
      recipeResolver: (slug, owner) => findRecipe(slug, findUserRecipe, owner),
      ...args,
    }));

  const onCommit = async () => {
    if (!draft?.id) return;
    setCommitting(true);
    try {
      const planRow = { ...draft, week_start: weekStartISO, household: { servings }, slots };
      const { scheduled, failed } = await commitWeeklyPlan({ plan: planRow, schedule: scheduleFn });
      await markCommitted();
      setCommitted(true);
      const rows = computeWeekShoppingItems({
        slots,
        resolveRecipe: resolveSlotRecipe,
        pantry,
        householdServings: servings,
        existingList: shoppingList,
      });
      setShoppingRows(rows);
      pushToast(
        failed.length
          ? `${scheduled} meals added; ${failed.length} failed`
          : `${scheduled} meals on the calendar`,
        { kind: failed.length ? "warn" : "info" },
      );
    } catch (e) {
      console.error("[weeklyPlanner] commit failed:", e);
      pushToast(e?.message || "Couldn't add the week — try again.", { kind: "warn" });
    } finally {
      setCommitting(false);
    }
  };

  const onAddShopping = () => {
    if (!shoppingRows?.length || !setShoppingList) return;
    setShoppingList((prev) => [...prev, ...shoppingRows]);
    setShoppingAdded(true);
    pushToast(`${shoppingRows.length} items added to your shopping list`, { kind: "info" });
  };

  // ── Waiting-stage ticker (planning phase) ─────────────────────────
  const WAIT_STAGES = [
    "Reading your pantry…",
    "Flipping through your recipe book…",
    "Weighing the week against your goal…",
    "Drafting menus for every slot…",
    "Binding ingredients to your kitchen…",
    "Nearly there — plating the week…",
  ];
  const [waitStage, setWaitStage] = useState(0);
  useEffect(() => {
    if (phase !== "planning") { setWaitStage(0); return undefined; }
    const t = setInterval(
      () => setWaitStage((i) => Math.min(i + 1, WAIT_STAGES.length - 1)),
      3200,
    );
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ── Render ────────────────────────────────────────────────────────
  const enabledCount = slots.filter((s) => s.enabled !== false).length;

  const shell = (
    <div style={{
      position: "fixed", inset: 0, zIndex: Z.sheet,
      background: COLOR.night,
      display: "flex", flexDirection: "column",
    }}>
      {/* Fixed header — the escape hatch lives here and never scrolls
          away, on every phase (setup / planning / review). */}
      <div style={{
        padding: "calc(14px + env(safe-area-inset-top, 0px)) 20px 12px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 12, borderBottom: `1px solid ${COLOR.edge}`, flexShrink: 0,
      }}>
        <div style={{
          fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
          color: COLOR.gold, letterSpacing: "0.16em", textTransform: "uppercase",
        }}>
          Weekly planner
        </div>
        <button
          className="mcm-focusable"
          onClick={onClose}
          style={{
            fontFamily: FONT.forma, fontSize: 13, fontWeight: 600,
            letterSpacing: "0.1em", color: COLOR.ink,
            background: COLOR.ground, border: `1px solid ${COLOR.border}`,
            borderRadius: RADIUS.pill, padding: "10px 18px",
            minHeight: 44, cursor: "pointer",
          }}
        >
          CLOSE
        </button>
      </div>

      <div style={{
        flex: 1, overflowY: "auto",
        padding: "20px 20px calc(48px + env(safe-area-inset-bottom, 0px))",
        WebkitOverflowScrolling: "touch",
        overscrollBehaviorY: "contain",
      }}>
      {phase === "planning" ? (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", minHeight: "60vh", textAlign: "center",
          padding: "0 12px",
        }}>
          <div style={{
            fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
            fontSize: 28, color: COLOR.ink, marginBottom: 18, lineHeight: 1.15,
          }}>
            Planning your week
          </div>
          {/* Stage dots — filled up to the current stage so the wait
              visibly moves even between copy changes. */}
          <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
            {WAIT_STAGES.map((_, i) => (
              <span key={i} style={{
                width: 8, height: 8, borderRadius: 4,
                background: i <= waitStage ? COLOR.gold : COLOR.goldDeep,
                transition: "background 0.4s ease",
              }} />
            ))}
          </div>
          <div key={waitStage} style={{
            fontFamily: FONT.forma, fontSize: 15, fontWeight: 600,
            color: COLOR.muted, marginBottom: 26, minHeight: 22,
          }}>
            {WAIT_STAGES[waitStage]}
          </div>
          <div style={{
            fontFamily: FONT.forma, fontSize: 13, color: COLOR.sub,
            lineHeight: 1.6, maxWidth: 300,
          }}>
            You can close this — the week keeps building and will be
            waiting right here when you come back.
          </div>
        </div>
      ) : phase === "review" ? (
        <WeeklyPlanReview
          weekStartISO={weekStartISO}
          slots={slots}
          weekNote={weekNote}
          digest={digest}
          busySlotKey={busySlotKey}
          lockedKeys={lockedKeys}
          committed={committed}
          committing={committing}
          shoppingCount={shoppingRows ? shoppingRows.length : null}
          shoppingAdded={shoppingAdded}
          onReroll={onReroll}
          onToggleLock={onToggleLock}
          onRemove={onRemove}
          onCommit={onCommit}
          onAddShopping={onAddShopping}
          onBack={() => setPhase("setup")}
          onDone={onClose}
        />
      ) : (
        <div>
          <div style={{
            fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
            fontSize: 28, color: COLOR.ink, marginBottom: 6, lineHeight: 1.15,
          }}>
            Plan your week
          </div>
          <div style={{ fontFamily: FONT.forma, fontSize: 14, color: COLOR.muted, marginBottom: 24, lineHeight: 1.5 }}>
            One pass fills every meal below — then you swap and reroll
            until it's yours.
          </div>

          <div style={kicker}>RECIPES FROM</div>
          <ChipRow
            options={PLANNER_SOURCES}
            value={sourceId}
            onChange={setSourceId}
            disabledIds={bookEmpty ? new Set(["book", "mixed"]) : new Set()}
          />
          {bookEmpty && (
            <div style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.sub, marginBottom: 6 }}>
              Your recipe book is empty — this week will be all new dishes.
            </div>
          )}
          {!bookOnly && (
            <>
              <div style={{ ...kicker, marginTop: 22 }}>NEW DISHES ARE</div>
              <ChipRow
                options={PLANNER_NEW_MODES}
                value={newMode}
                onChange={setNewMode}
                disabledIds={pantryEmpty ? new Set(["pantry"]) : new Set()}
              />
              {pantryEmpty && (
                <div style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.sub, marginBottom: 6 }}>
                  Pantry-first needs items in your kitchen first.
                </div>
              )}
            </>
          )}

          <div style={{ ...kicker, marginTop: 26 }}>THE GOAL</div>
          <ChipRow options={PLANNER_GOALS} value={goal} onChange={setGoal} />
          <div style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.sub, marginBottom: 6 }}>
            {PLANNER_GOALS.find((g) => g.id === goal)?.blurb}
          </div>

          <div style={{ ...kicker, marginTop: 22 }}>BUDGET FEEL</div>
          <ChipRow options={PLANNER_BUDGETS} value={budget} onChange={setBudget} />

          <div style={{ ...kicker, marginTop: 26 }}>COOKING FOR</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <ChipRow
              options={[
                { id: "me", label: "JUST ME" },
                { id: "family", label: "FAMILY" },
              ]}
              value={servings === 1 ? "me" : "family"}
              onChange={(id) =>
                setServings(id === "me" ? 1 : Math.max(2, familyCount + 1))}
            />
            {servings > 1 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {["-", "+"].map((op) => (
                  <button
                    key={op}
                    className="mcm-focusable"
                    onClick={() =>
                      setServings((v) => Math.max(2, Math.min(12, v + (op === "+" ? 1 : -1))))}
                    style={{
                      width: 30, height: 30, borderRadius: 15,
                      background: COLOR.deep, border: `1px solid ${COLOR.border}`,
                      fontFamily: FONT.forma, fontSize: 15, color: COLOR.ink, cursor: "pointer",
                    }}
                  >
                    {op}
                  </button>
                ))}
                <span style={{ fontFamily: FONT.forma, fontSize: 14, fontWeight: 600, color: COLOR.ink }}>
                  {servings} servings
                </span>
              </div>
            )}
          </div>

          <div style={{ ...kicker, marginTop: 26 }}>MEALS TO PLAN ({enabledCount})</div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "56px repeat(7, 1fr)",
            gap: 4, marginBottom: 14, alignItems: "center",
          }}>
            <span />
            {Array.from({ length: 7 }, (_, d) => (
              <span key={d} style={{
                fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
                color: COLOR.sub, textAlign: "center", letterSpacing: "0.04em",
              }}>
                {dayLabelFor(weekStartISO, d).slice(0, 2)}
              </span>
            ))}
            {MEAL_SLOTS.map((slot) => (
              <FragmentRow
                key={slot}
                slot={slot}
                slots={slots}
                onToggle={(day) => {
                  updateSlots(slots.map((s) =>
                    s.day === day && s.slot === slot
                      ? { ...s, enabled: s.enabled === false }
                      : s,
                  ));
                }}
              />
            ))}
          </div>

          {planError && (
            <div style={{
              fontFamily: FONT.forma, fontSize: 13, color: COLOR.rose,
              background: COLOR.roseDeep, border: `1px solid ${COLOR.roseDim}`,
              borderRadius: RADIUS.lg, padding: "10px 12px", marginBottom: 10,
            }}>
              {planError}
            </div>
          )}

          <button
            className="mcm-focusable"
            onClick={runPlan}
            disabled={enabledCount === 0}
            style={{
              width: "100%",
              fontFamily: FONT.forma, fontSize: 14, fontWeight: 600,
              letterSpacing: "0.12em", color: COLOR.ink,
              background: COLOR.goldDeep,
              border: `1px solid ${COLOR.goldDim}`,
              borderRadius: RADIUS.xl, padding: "14px 16px",
              cursor: "pointer",
              opacity: enabledCount === 0 ? 0.5 : 1,
            }}
          >
            PLAN MY WEEK
          </button>
          <div style={{
            fontFamily: FONT.forma, fontSize: 12, color: COLOR.sub,
            textAlign: "center", marginTop: 8,
          }}>
            Uses 6 AI tokens. Rerolls cost 2. Full recipes generate later, per dish.
          </div>
          {draft?.slots?.some((s) => s?.source) && (
            <button
              onClick={() => { discardDraft(); onClose?.(); }}
              style={{
                width: "100%", background: "transparent", border: "none",
                fontFamily: FONT.forma, fontSize: 12, color: COLOR.sub,
                letterSpacing: "0.08em", padding: "12px 0", cursor: "pointer",
              }}
            >
              DISCARD DRAFT WEEK
            </button>
          )}
        </div>
      )}
      </div>
    </div>
  );
  return createPortal(shell, document.body);
}

// One toggle row of the 7×3 slot grid (label + 7 cells).
function FragmentRow({ slot, slots, onToggle }) {
  return (
    <>
      <span style={{
        fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
        color: COLOR.muted, letterSpacing: "0.04em",
      }}>
        {SLOT_LABELS[slot].slice(0, 5)}
      </span>
      {Array.from({ length: 7 }, (_, day) => {
        const s = slots.find((x) => x.day === day && x.slot === slot);
        const on = s?.enabled !== false;
        return (
          <button
            key={day}
            className="mcm-focusable"
            onClick={() => onToggle(day)}
            aria-label={`${slot} day ${day + 1} ${on ? "on" : "off"}`}
            style={{
              height: 26, borderRadius: RADIUS.sm,
              background: on ? COLOR.leafDeep : COLOR.deep,
              border: `1px solid ${on ? COLOR.leafDim : COLOR.edge}`,
              cursor: "pointer",
            }}
          />
        );
      })}
    </>
  );
}
